import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { peopleAPI } from '../services/api';
import { encountersAPI } from '../services/api';
import { getProfilePicture, getFirstName } from '../utils/helpers';
import { FaMapMarkerAlt, FaHeart, FaStar, FaRedo, FaFilter, FaFire, FaCog, FaBell, FaUser, FaSignOutAlt } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import FilterModal from '../components/FilterModal';
import Ads from '../components/Ads';
import Header from '../components/Header';

const People = () => {
  const navigate = useNavigate();
  const { user, logout: authLogout } = useAuth();

  // Debug user changes
  useEffect(() => {
    console.log('👤 People Header - User changed:', user?.id, user?.profile_photo, user?.full_name);
  }, [user]);

  const logout = async () => {
    try {
      await authLogout();
      navigate('/login');
    } catch (error) {
      console.error('People: Logout error:', error);
    }
  };
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    gender: '',
    country: '',
    city: '',
    age_min: '',
    age_max: '',
    order_by: 'random'
  });
  const [isFiltered, setIsFiltered] = useState(false);
  const [showNearby, setShowNearby] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);

  const limit = 20; // Sayfa başına kişi sayısı

  useEffect(() => {
    fetchPeople(true);
    // Scroll to top (header) when page loads
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  }, [filters]); // Add filters dependency

  const fetchPeople = async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setPage(1);
      } else {
        setLoadingMore(true);
      }

      const currentPage = reset ? 1 : page;

      let response;
      if (isFiltered && Object.values(filters).some(value => value !== '' && value !== 'random')) {
        // Use filtered search when filters are applied
        const filterParams = {
          page: currentPage,
          limit,
          ...filters
        };
        response = await peopleAPI.getPeople(currentPage, filterParams);
        console.log('People API response (filtered):', response);
      } else {
        // Use random people when no filters
        response = await peopleAPI.getRandomPeople(limit);
        console.log('People API response (random):', response);
      }

      const newPeople = response.data || response.people || [];
      console.log('New people count:', newPeople.length);

      if (reset) {
        setPeople(newPeople);
      } else {
        setPeople(prev => [...prev, ...newPeople]);
        setPage(currentPage + 1);
      }

      // Check pagination info
      const pagination = response.pagination;
      if (pagination) {
        setHasMore(pagination.hasMore);
      } else {
        // For random people, disable load more
        setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to fetch people:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleRefresh = () => {
    console.log('Refresh button clicked - refreshing only cards');
    fetchPeople(true);
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore && !showNearby) {
      fetchPeople(false);
    }
  };

  const handleFilterApply = (appliedFilters) => {
    console.log('Applied filters:', appliedFilters);

    // Convert FilterModal format to API format
    const apiFilters = {
      gender: appliedFilters.sexual_preference === '1' ? 'Male' :
              appliedFilters.sexual_preference === '2' ? 'Female' : '',
      country: appliedFilters.country || '',
      city: '', // FilterModal doesn't have city field
      age_min: appliedFilters.age_range ? appliedFilters.age_range[0] : '',
      age_max: appliedFilters.age_range ? appliedFilters.age_range[1] : '',
      order_by: appliedFilters.order_by === '1' ? 'random' :
                appliedFilters.order_by === '2' ? 'newest' :
                appliedFilters.order_by === '3' ? 'popular' :
                appliedFilters.order_by === '4' ? 'online' : 'random'
    };

    setFilters(apiFilters);
    setIsFiltered(true);
    setShowNearby(false); // Disable nearby when using filters
    setShowFilters(false);
    fetchPeople(true);
  };

  const handleNearbySearch = async () => {
    try {
      setLocationLoading(true);

      // Get user's current location
      if (!navigator.geolocation) {
        alert('Geolocation is not supported by this browser.');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ latitude, longitude });

          // Fetch nearby people
          const response = await peopleAPI.getNearbyPeople(latitude, longitude, 50, 1, limit);
          console.log('Nearby people response:', response);

          const nearbyPeople = response.data || [];
          setPeople(nearbyPeople);
          setIsFiltered(false);
          setShowNearby(true);
          setPage(1);
          setHasMore(response.pagination?.hasMore || false);
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Unable to get your location. Please check your browser settings.');
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } catch (error) {
      console.error('Nearby search error:', error);
      alert('Failed to find nearby people.');
    } finally {
      setLocationLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      gender: '',
      country: '',
      city: '',
      age_min: '',
      age_max: '',
      order_by: 'random'
    });
    setIsFiltered(false);
    setShowNearby(false);
    setUserLocation(null);
    fetchPeople(true);
  };

  // Loading state for refresh - only affects the cards area

    return (
    <div className="min-h-screen bg-white">
      <Header title="People" />

      {/* Main Content */}
      <div className="matchus-main-content">
      <div className="matchus-discovery">
          {/* Page Header - Action Buttons */}
          <div className="matchus-page-header">
            <div className="matchus-page-header-content">
              <div className="flex space-x-2">
                <button
                  type="button"
                  className="matchus-refresh-btn"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <FaRedo className={loading ? 'animate-spin' : ''} />
                  <span>{loading ? 'Refreshing...' : 'Refresh'}</span>
                </button>

                <button
                  type="button"
                  className={`matchus-location-btn ${showNearby ? 'bg-blue-500 text-white' : ''}`}
                  onClick={handleNearbySearch}
                  disabled={locationLoading || loading}
                >
                  <FaMapMarkerAlt className={locationLoading ? 'animate-pulse' : ''} />
                  <span>{locationLoading ? 'Getting location...' : showNearby ? 'Nearby' : 'Find Nearby'}</span>
                </button>

                {(isFiltered || showNearby) && (
                  <button
                    type="button"
                    className="matchus-clear-btn"
                    onClick={handleClearFilters}
                    disabled={loading}
                  >
                    <FaTimes />
                    <span>Clear Filters</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Ads */}
          <Ads location="search_page" limit={1} />

      {/* People Grid */}
      {loading ? (
        <div className="matchus-discovery-loading">
          <div className="matchus-spinner"></div>
          <p>Finding amazing people...</p>
        </div>
      ) : people.length === 0 ? (
        <div className="matchus-discovery-empty">
          <div className="matchus-empty-icon">🔍</div>
          <h2>No people found</h2>
          <p>Try adjusting your filters to find more matches</p>
          <button
            type="button"
            className="matchus-retry-btn"
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh List
          </button>
        </div>
      ) : (
        <>
          <div className="matchus-people-grid">
            {people.map((person) => (
              <Link
                key={person.id}
                to={`/user/profile/${person.id}`}
                className="matchus-person-card"
              >
                <div className="matchus-person-photo">
                  <img
                    src={getProfilePicture(person)}
                    alt={person.full_name}
                    className="matchus-person-image"
                  />
                  <div className="matchus-person-overlay">
                    <div className="matchus-person-info">
                      <div className="matchus-person-header">
                        <h3 className="matchus-person-name">
                          {getFirstName(person.full_name)}, {person.age}
                          {person.isOnline && (
                            <span className="matchus-person-online"></span>
                          )}
                        </h3>
                        {person.score && (
                          <div className="matchus-match-score">
                            <FaStar className="matchus-star-icon" />
                            <span>{person.score}%</span>
                          </div>
                        )}
                      </div>
                      <div className="matchus-person-location">
                        <FaMapMarkerAlt />
                        <span>{person.city}{person.city && person.country && ', '}{person.country}</span>
                      </div>
                      {person.interests && person.interests.split(',').length > 0 && (
                        <div className="matchus-person-interests">
                          {person.interests.split(',').slice(0, 2).map((interest, index) => (
                            <span key={index} className="matchus-interest-tag">
                              {interest.trim()}
                            </span>
                          ))}
                          {person.interests.split(',').length > 2 && (
                            <span className="matchus-interest-more">
                              +{person.interests.split(',').length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Load More */}
          {hasMore && (
            <div className="matchus-load-more">
              {loadingMore ? (
                <div className="matchus-loading-more">
                  <div className="matchus-spinner-small"></div>
                  <span>Loading more...</span>
                </div>
              ) : (
                <button
                  className="matchus-load-more-btn"
                  onClick={handleLoadMore}
                >
                  Load More
                </button>
              )}
            </div>
          )}
        </>
      )}

          {/* Filter Modal */}
          <FilterModal
            isOpen={showFilters}
            onClose={() => setShowFilters(false)}
            onApply={handleFilterApply}
            currentFilters={filters}
          />

        </div>
      </div>
    </div>
  );
};

export default People;

